import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash, PencilSimple, Key, Warning, Link as LinkIcon,
  DownloadSimple, PaperPlaneTilt, UploadSimple, X, FilePdf, File as FileIcon,
  Archive, ArrowCounterClockwise, Image as ImageIcon, MagnifyingGlass, Copy,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";
import { absTime } from "../lib/labels";

function attachmentUrl(attId) {
  const token = localStorage.getItem("helios_token");
  const base = `${API_BASE}/software-licenses/attachments/${attId}`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}

function imageUrl(itemId, cacheBuster) {
  // We authenticate via ?token= so <img src> works without JS; cacheBuster forces reload
  // right after an upload (browsers cache aggressively on identical URLs).
  const token = localStorage.getItem("helios_token");
  const base = `${API_BASE}/software-licenses/${itemId}/image`;
  const qs = [];
  if (token) qs.push(`token=${encodeURIComponent(token)}`);
  if (cacheBuster) qs.push(`v=${encodeURIComponent(cacheBuster)}`);
  return qs.length ? `${base}?${qs.join("&")}` : base;
}

const emptyForm = { title: "", description: "", official_url: "", download_url: "", licenses: [], seats_total: "", seats_used_manual: "", low_stock_threshold: 2 };
const emptyLicense = { label: "", key: "", expires_at: "", notes: "" };

/** Small "N/M disponibles" badge used on cards + detail. Color-coded by health.
 * Optionally renders inline +/- buttons for fast attribution/return of a licence. */
const SeatsBadge = ({ remaining, used, total, threshold = 2, dense = false, onAdjust }) => {
  if (total === null || total === undefined || total === "" || Number.isNaN(Number(total))) return null;
  const rem = remaining ?? Math.max(0, Number(total) - (Number(used) || 0));
  const th = Number.isFinite(Number(threshold)) ? Number(threshold) : 2;
  let cls = "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300";
  if (rem === 0) cls = "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300";
  else if (rem <= th) cls = "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300";
  return (
    <span data-testid="sl-seats-badge" className="inline-flex items-center gap-0.5">
      {onAdjust && (
        <button
          onClick={(e) => { e.stopPropagation(); onAdjust(1); }}
          disabled={rem === 0}
          title="Attribuer une licence (−1 dispo)"
          data-testid="sl-seats-minus"
          className={`inline-flex items-center justify-center rounded-l-full font-bold ${dense ? "h-5 w-5 text-sm" : "h-6 w-6 text-base"} ${cls} hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed`}
        >−</button>
      )}
      <span
        className={`inline-flex items-center ${dense ? "text-[0.65rem] px-1.5 py-0.5" : "text-xs px-2 py-0.5"} ${onAdjust ? "" : "rounded-full"} font-semibold ${cls}`}
        title={rem <= th ? `Stock bas — pensez à commander (seuil ${th})` : ""}
      >
        {rem}/{total} dispo
      </span>
      {onAdjust && (
        <button
          onClick={(e) => { e.stopPropagation(); onAdjust(-1); }}
          disabled={rem >= Number(total)}
          title="Restituer une licence (+1 dispo)"
          data-testid="sl-seats-plus"
          className={`inline-flex items-center justify-center rounded-r-full font-bold ${dense ? "h-5 w-5 text-sm" : "h-6 w-6 text-base"} ${cls} hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed`}
        >+</button>
      )}
    </span>
  );
};

const expiryBadge = (iso) => {
  if (!iso) return null;
  const days = Math.floor((new Date(iso).getTime() - Date.now()) / 86400000);
  if (isNaN(days)) return null;
  if (days < 0) return <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 font-semibold">Expiré {Math.abs(days)}j</span>;
  if (days < 30) return <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 font-semibold">Expire dans {days}j</span>;
  return <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">{days}j restants</span>;
};

export default function SoftwareLicenses() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [techs, setTechs] = useState([]);
  const [editor, setEditor] = useState(null); // { mode:'create'|'edit', id?, ...form }
  const [selected, setSelected] = useState(null); // detail view
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [emailModal, setEmailModal] = useState(null); // { item, tech_id, note }
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = () => {
    const params = new URLSearchParams();
    if (showArchived) params.set("archived", "true");
    if (qDebounced) params.set("q", qDebounced);
    const qs = params.toString();
    return api.get(`/software-licenses${qs ? `?${qs}` : ""}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    setSelectedIds(new Set()); // clear selection whenever the list changes
  }, [showArchived, qDebounced]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/users?role=technicien").then((r) => setTechs(r.data)).catch(() => {
      // Fallback if the query filter isn't supported
      api.get("/users").then((r) => setTechs((r.data || []).filter((u) => ["admin", "technicien"].includes(u.role)))).catch(() => {});
    });
  }, []);

  const openCreate = () => setEditor({ mode: "create", ...emptyForm, licenses: [{ ...emptyLicense }], pending_files: [] });
  const openEdit = (it) => setEditor({
    mode: "edit", id: it.id,
    title: it.title, description: it.description || "",
    official_url: it.official_url || "", download_url: it.download_url || "",
    seats_total: it.seats_total ?? "",
    seats_used_manual: it.seats_used_manual ?? "",
    low_stock_threshold: it.low_stock_threshold ?? 2,
    licenses: (it.licenses || []).length ? it.licenses.map((l) => ({ ...emptyLicense, ...l })) : [{ ...emptyLicense }],
    pending_files: [],
  });

  const saveEditor = async () => {
    if (!editor.title || editor.title.length < 2) { toast.error("Titre requis"); return; }
    const licenses = editor.licenses.filter((l) => l.key && l.key.trim());
    setBusy(true);
    try {
      const body = {
        title: editor.title.trim(),
        description: editor.description || "",
        official_url: editor.official_url || null,
        download_url: editor.download_url || null,
        licenses,
        // "" → null : capacity tracking off. Any parsable number → int.
        seats_total: editor.seats_total === "" || editor.seats_total === null
          ? null : Math.max(0, parseInt(editor.seats_total, 10) || 0),
        seats_used_manual: editor.seats_used_manual === "" || editor.seats_used_manual === null
          ? null : Math.max(0, parseInt(editor.seats_used_manual, 10) || 0),
        low_stock_threshold: Math.max(0, parseInt(editor.low_stock_threshold, 10) || 0),
      };
      let saved;
      if (editor.mode === "create") {
        saved = (await api.post("/software-licenses", body)).data;
        toast.success("Fiche créée");
      } else {
        saved = (await api.put(`/software-licenses/${editor.id}`, body)).data;
        toast.success("Fiche mise à jour");
      }
      // v295 — Upload des fichiers mis en file d'attente pendant la saisie
      // (indispensable à la création, confort à l'édition). Un échec sur l'un
      // n'empêche pas les autres de monter, on log juste le fichier fautif.
      const pending = editor.pending_files || [];
      if (pending.length) {
        let okCount = 0, failed = [];
        for (const f of pending) {
          try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post(`/software-licenses/${saved.id}/attachments`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            okCount += 1;
          } catch (err) {
            failed.push(f.name);
          }
        }
        if (okCount) toast.success(`${okCount} fichier(s) uploadé(s)`);
        if (failed.length) toast.error(`Échec upload : ${failed.join(", ")}`);
        // Refresh pour afficher les PJ dans la fiche détail juste après
        saved = (await api.get(`/software-licenses/${saved.id}`)).data;
      }
      setEditor(null);
      await load();
      setSelected(saved);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    const id = confirmDelete.id;
    setConfirmDelete(null);
    try {
      await api.delete(`/software-licenses/${id}`);
      toast.success("Fiche supprimée");
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  // v289 — Copie une fiche existante (titre + description + URLs + seats_total
  // + low_stock_threshold) sans importer les clés déjà attribuées, les pièces
  // jointes, l'image ou le compteur manuel. Ouvre directement l'éditeur sur
  // la fiche dupliquée pour que l'utilisateur puisse ajuster ce qu'il faut.
  const doDuplicate = async (item) => {
    try {
      const r = await api.post(`/software-licenses/${item.id}/duplicate`);
      toast.success("Fiche dupliquée — vous pouvez la modifier");
      setSelected(null);
      await load();
      openEdit(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const uploadFile = async (item, ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    ev.target.value = "";
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/software-licenses/${item.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Fichier uploadé");
      const r = await api.get(`/software-licenses/${item.id}`);
      setSelected(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const deleteAttachment = async (item, attId) => {
    try {
      await api.delete(`/software-licenses/${item.id}/attachments/${attId}`);
      toast.success("Fichier supprimé");
      const r = await api.get(`/software-licenses/${item.id}`);
      setSelected(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const sendEmail = async () => {
    if (!emailModal?.tech_id) { toast.error("Sélectionne un technicien"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/software-licenses/${emailModal.item.id}/email`, {
        tech_id: emailModal.tech_id,
        note: emailModal.note || null,
      });
      toast.success(`Email envoyé à ${r.data.sent_to}`);
      setEmailModal(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const adjustSeats = async (item, delta) => {
    // Optimistic no-op: refresh the list on success. Delta is +1 to consume, -1 to restore.
    try {
      const r = await api.post(`/software-licenses/${item.id}/seats/adjust`, { delta });
      // Merge the updated fields into local state so cards + modal reflect instantly.
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, ...r.data } : x)));
      if (selected?.id === item.id) setSelected((s) => ({ ...s, ...r.data }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const toggleArchive = async (item, wantArchived) => {
    try {
      await api.post(`/software-licenses/${item.id}/archive`, { archived: wantArchived });
      toast.success(wantArchived ? "Fiche archivée" : "Fiche restaurée");
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const uploadImage = async (item, ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    ev.target.value = "";
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop volumineuse (max 5 Mo)"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/software-licenses/${item.id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image mise à jour");
      const r = await api.get(`/software-licenses/${item.id}`);
      setSelected(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const removeImage = async (item) => {
    try {
      await api.delete(`/software-licenses/${item.id}/image`);
      toast.success("Image supprimée");
      const r = await api.get(`/software-licenses/${item.id}`);
      setSelected(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="p-6 max-w-6xl">
      <button onClick={() => nav("/modules")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="sl-back">
        <ArrowLeft size={16}/> Modules
      </button>
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-bold mb-1 flex items-center gap-2"><Key size={26}/> Licences logiciel</h1>
          <p className="text-sm text-muted-foreground">Registre centralisé — clés + expirations + PDF + activateurs + liens officiels. Envoi par email à un technicien en 1 clic.</p>
        </div>
        <div className="flex gap-2">
          <Button variant={showArchived ? "default" : "outline"}
                  onClick={() => setShowArchived(!showArchived)}
                  data-testid="sl-toggle-archived">
            <Archive size={14} className="mr-1"/>{showArchived ? "Voir les actives" : "Voir les archives"}
          </Button>
          <Button onClick={openCreate} data-testid="sl-new-btn"><Plus size={16} className="mr-1"/> Nouvelle fiche</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Rechercher par titre, description, libellé ou clé de licence…"
                 className="pl-8" data-testid="sl-search"/>
        </div>
        <div className="text-xs text-muted-foreground shrink-0" data-testid="sl-total">
          {items.length} fiche{items.length > 1 ? "s" : ""}
          {selectedIds.size > 0 && <span className="ml-2 text-primary font-semibold">· {selectedIds.size} sélectionnée{selectedIds.size > 1 ? "s" : ""}</span>}
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2" data-testid="sl-bulk-bar">
          <span className="text-sm font-semibold">{selectedIds.size} sélectionnée{selectedIds.size > 1 ? "s" : ""}</span>
          <div className="flex-1"/>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>Désélectionner</Button>
          <Button variant={showArchived ? "outline" : "destructive"} size="sm"
                  onClick={() => setConfirmBulk(true)}
                  data-testid="sl-bulk-archive-btn">
            {showArchived
              ? <><ArrowCounterClockwise size={14} className="mr-1"/> Restaurer ({selectedIds.size})</>
              : <><Archive size={14} className="mr-1"/> Archiver ({selectedIds.size})</>}
          </Button>
        </div>
      )}

      {items.length === 0 && (
        <div className="rounded-xl border-2 border-dashed p-8 text-center text-muted-foreground" data-testid="sl-empty">
          {qDebounced ? "Aucune fiche ne correspond à cette recherche." : (showArchived ? "Aucune fiche archivée." : "Aucune fiche pour l'instant. Créez la première.")}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => {
          const anyExpiring = (it.licenses || []).some((l) => {
            if (!l.expires_at) return false;
            const days = Math.floor((new Date(l.expires_at).getTime() - Date.now()) / 86400000);
            return days < 30;
          });
          const isChecked = selectedIds.has(it.id);
          return (
            <div key={it.id}
                 className={"relative rounded-xl border bg-card p-4 flex gap-3 hover:border-primary/50 transition-colors " + (isChecked ? "border-primary/60 bg-primary/5" : "")}
                 data-testid={`sl-card-${it.id}`}>
              <input type="checkbox" checked={isChecked}
                     onChange={(e) => {
                       e.stopPropagation();
                       const s = new Set(selectedIds);
                       isChecked ? s.delete(it.id) : s.add(it.id);
                       setSelectedIds(s);
                     }}
                     onClick={(e) => e.stopPropagation()}
                     className="absolute top-2 right-2 cursor-pointer w-4 h-4"
                     data-testid={`sl-select-${it.id}`}
                     aria-label="Sélectionner la fiche"/>
              <button onClick={() => setSelected(it)} className="text-left flex gap-3 flex-1 min-w-0">
                {it.image_path ? (
                  <img src={imageUrl(it.id, it.updated_at)}
                       alt={`Logo ${it.title}`}
                       className="w-14 h-14 rounded-lg object-cover border shrink-0 bg-muted"
                       onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                ) : (
                  <div className="w-14 h-14 rounded-lg border shrink-0 bg-muted/40 flex items-center justify-center text-muted-foreground">
                    <ImageIcon size={22}/>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2 pr-6">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate flex items-center gap-1.5">
                        {it.title}
                        {it.archived && <span className="text-[0.6rem] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 font-semibold">ARCHIVÉ</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span>{(it.licenses || []).length} licence(s) · {(it.attachments || []).length} PJ</span>
                        <SeatsBadge remaining={it.seats_remaining} used={it.seats_used} total={it.seats_total}
                                    threshold={it.low_stock_threshold} dense
                                    onAdjust={(d) => adjustSeats(it, d)}/>
                      </div>
                    </div>
                    {anyExpiring && <Warning size={16} className="text-amber-600 shrink-0"/>}
                  </div>
                  {(it.description || "").trim() && (
                    <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{it.description}</div>
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {/* -------- Detail modal -------- */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Key size={20}/> {selected.title}
                  {selected.archived && <span className="text-[0.65rem] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 font-semibold ml-1">ARCHIVÉ</span>}
                </DialogTitle>
                <DialogDescription>Créée par {selected.author_name} · MAJ {absTime(selected.updated_at)}</DialogDescription>
              </DialogHeader>
              {/* Product image + upload/replace */}
              <div className="flex items-start gap-3">
                {selected.image_path ? (
                  <img src={imageUrl(selected.id, selected.updated_at)}
                       alt={`Logo ${selected.title}`}
                       className="w-24 h-24 rounded-lg object-cover border bg-muted"/>
                ) : (
                  <div className="w-24 h-24 rounded-lg border bg-muted/40 flex items-center justify-center text-muted-foreground">
                    <ImageIcon size={30}/>
                  </div>
                )}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-primary hover:underline cursor-pointer inline-flex items-center gap-1" data-testid="sl-image-upload">
                    <UploadSimple size={12}/> {selected.image_path ? "Remplacer la photo" : "Ajouter une photo"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                           onChange={(e) => uploadImage(selected, e)}/>
                  </label>
                  {selected.image_path && (
                    <button onClick={() => removeImage(selected)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1" data-testid="sl-image-remove">
                      <Trash size={12}/> Retirer la photo
                    </button>
                  )}
                  <div className="text-[0.65rem] text-muted-foreground">PNG/JPG/WEBP · max 5 Mo</div>
                </div>
              </div>
              {(selected.description || "").trim() && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Description</div>
                  <div className="text-sm whitespace-pre-wrap">{selected.description}</div>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center justify-between gap-2">
                  <span>Stock</span>
                  <SeatsBadge remaining={selected.seats_remaining} used={selected.seats_used}
                              total={selected.seats_total} threshold={selected.low_stock_threshold}
                              onAdjust={(d) => adjustSeats(selected, d)}/>
                </div>
                <div className="text-xs text-muted-foreground">
                  {(selected.seats_total === null || selected.seats_total === undefined)
                    ? "Aucun stock configuré — édite la fiche pour renseigner un nombre total."
                    : <>Utilise <span className="mono">−</span> quand tu attribues une licence, <span className="mono">+</span> pour en récupérer une. Un email d'alerte partira dès que le stock passera sous <b>{selected.low_stock_threshold ?? 2}</b>.</>}
                </div>
              </div>
              {(selected.licenses || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center justify-between gap-2">
                    <span>Numéros de licence</span>
                  </div>
                  <div className="space-y-2">
                    {selected.licenses.map((l, i) => (
                      <div key={i} className="rounded-lg border p-3 bg-muted/30" data-testid={`sl-lic-${i}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="font-medium text-sm">{l.label || `Licence ${i + 1}`}</div>
                          {expiryBadge(l.expires_at)}
                        </div>
                        <div className="mono text-xs bg-background border rounded px-2 py-1 mt-2 select-all break-all">{l.key}</div>
                        {l.notes && <div className="text-xs text-muted-foreground mt-1">{l.notes}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(selected.official_url || selected.download_url) && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Liens</div>
                  <div className="flex flex-wrap gap-2">
                    {selected.official_url && (
                      <a href={selected.official_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="sl-official-url">
                        <LinkIcon size={14}/> Site officiel
                      </a>
                    )}
                    {selected.download_url && (
                      <a href={selected.download_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="sl-download-url">
                        <DownloadSimple size={14}/> Téléchargement
                      </a>
                    )}
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Pièces jointes</div>
                  <label className="text-xs text-primary hover:underline cursor-pointer inline-flex items-center gap-1" data-testid="sl-upload-label">
                    <UploadSimple size={12}/> Uploader
                    <input type="file" className="hidden" onChange={(e) => uploadFile(selected, e)}/>
                  </label>
                </div>
                {(selected.attachments || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">Aucune pièce jointe.</div>
                ) : (
                  <div className="space-y-1">
                    {selected.attachments.map((a) => {
                      const isPdf = (a.content_type || "").includes("pdf") || a.original_filename?.toLowerCase().endsWith(".pdf");
                      return (
                        <div key={a.id} className="flex items-center gap-2 py-1.5 border-b last:border-0 text-sm" data-testid={`sl-att-${a.id}`}>
                          {isPdf ? <FilePdf size={16} className="text-red-600"/> : <FileIcon size={16} className="text-slate-500"/>}
                          <a href={attachmentUrl(a.id)} target="_blank" rel="noopener noreferrer" className="flex-1 truncate hover:underline">
                            {a.original_filename}
                          </a>
                          <span className="text-xs text-muted-foreground shrink-0">{Math.round((a.size || 0) / 1024)} Ko</span>
                          <button onClick={() => deleteAttachment(selected, a.id)} className="text-red-600 hover:bg-red-50 p-1 rounded" title="Supprimer">
                            <Trash size={14}/>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => setEmailModal({ item: selected, tech_id: "", note: "" })} data-testid="sl-email-btn">
                  <PaperPlaneTilt size={14} className="mr-1"/> Envoyer par email
                </Button>
                <Button variant="outline" onClick={() => { openEdit(selected); setSelected(null); }} data-testid="sl-edit-btn">
                  <PencilSimple size={14} className="mr-1"/> Modifier
                </Button>
                <Button variant="outline" onClick={() => doDuplicate(selected)} data-testid="sl-duplicate-btn">
                  <Copy size={14} className="mr-1"/> Dupliquer
                </Button>
                {selected.archived ? (
                  <Button variant="outline" onClick={() => toggleArchive(selected, false)} data-testid="sl-unarchive-btn">
                    <ArrowCounterClockwise size={14} className="mr-1"/> Restaurer
                  </Button>
                ) : (
                  <Button variant="outline" onClick={() => toggleArchive(selected, true)} data-testid="sl-archive-btn">
                    <Archive size={14} className="mr-1"/> Archiver
                  </Button>
                )}
                {isAdmin && (
                  <Button variant="destructive" onClick={() => setConfirmDelete(selected)} data-testid="sl-delete-btn">
                    <Trash size={14} className="mr-1"/> Supprimer
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* -------- Editor modal -------- */}
      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editor?.mode === "create" ? "Nouvelle fiche licence" : "Modifier la fiche"}</DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-4">
              <div>
                <Label>Titre du logiciel</Label>
                <Input value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value })} placeholder="Ex: Microsoft Office Pro 2024" data-testid="sl-editor-title"/>
              </div>
              <div>
                <Label>Description (procédure d'installation, contexte…)</Label>
                <Textarea value={editor.description} onChange={(e) => setEditor({ ...editor, description: e.target.value })} rows={4} placeholder="Description du logiciel, procédure, notes internes…" data-testid="sl-editor-description"/>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Site officiel (optionnel)</Label>
                  <Input value={editor.official_url} onChange={(e) => setEditor({ ...editor, official_url: e.target.value })} placeholder="https://microsoft.com/…"/>
                </div>
                <div>
                  <Label>Lien de téléchargement (optionnel)</Label>
                  <Input value={editor.download_url} onChange={(e) => setEditor({ ...editor, download_url: e.target.value })} placeholder="https://download.microsoft.com/…"/>
                </div>
              </div>
              <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Stock &amp; alerte</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Nombre total de licences achetées</Label>
                    <Input type="number" min="0" step="1"
                           value={editor.seats_total ?? ""}
                           onChange={(e) => setEditor({ ...editor, seats_total: e.target.value })}
                           placeholder="Ex. 5 (laisser vide pour ne pas suivre le stock)"
                           data-testid="sl-editor-seats-total"/>
                    <div className="text-[0.65rem] text-muted-foreground mt-1">
                      Le compteur restant se calcule automatiquement : total − licences activées.
                    </div>
                  </div>
                  <div>
                    <Label>Licences déjà attribuées</Label>
                    <Input type="number" min="0" step="1"
                           value={editor.seats_used_manual ?? ""}
                           onChange={(e) => setEditor({ ...editor, seats_used_manual: e.target.value })}
                           placeholder="Laisser vide = compte auto (len des clés ajoutées ci-dessous)"
                           data-testid="sl-editor-seats-used"/>
                    <div className="text-[0.65rem] text-muted-foreground mt-1">
                      Ou utilise les boutons − / + sur le badge de la card pour décompter facilement.
                    </div>
                  </div>
                  <div>
                    <Label>Seuil d'alerte email admin (licences restantes)</Label>
                    <Input type="number" min="0" step="1"
                           value={editor.low_stock_threshold ?? 2}
                           onChange={(e) => setEditor({ ...editor, low_stock_threshold: e.target.value })}
                           placeholder="Défaut : 2"
                           data-testid="sl-editor-threshold"/>
                    <div className="text-[0.65rem] text-muted-foreground mt-1">
                      À partir de ce nombre, un mail part aux admins pour recommander des licences (1 seul mail par franchissement).
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Numéros de licence</Label>
                  <Button variant="ghost" size="sm" onClick={() => setEditor({ ...editor, licenses: [...editor.licenses, { ...emptyLicense }] })} data-testid="sl-lic-add">
                    <Plus size={14} className="mr-1"/> Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {editor.licenses.map((l, i) => (
                    <div key={i} className="rounded-lg border p-3 space-y-2 bg-muted/20">
                      <div className="flex items-start gap-2">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 flex-1">
                          <Input placeholder="Libellé (Poste Alice…)" value={l.label || ""} onChange={(e) => {
                            const licenses = [...editor.licenses]; licenses[i] = { ...l, label: e.target.value };
                            setEditor({ ...editor, licenses });
                          }}/>
                          <Input placeholder="Clé de licence" value={l.key || ""} onChange={(e) => {
                            const licenses = [...editor.licenses]; licenses[i] = { ...l, key: e.target.value };
                            setEditor({ ...editor, licenses });
                          }} data-testid={`sl-lic-key-${i}`}/>
                          <Input type="date" value={l.expires_at || ""} onChange={(e) => {
                            const licenses = [...editor.licenses]; licenses[i] = { ...l, expires_at: e.target.value };
                            setEditor({ ...editor, licenses });
                          }}/>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => {
                          const licenses = editor.licenses.filter((_, j) => j !== i);
                          setEditor({ ...editor, licenses });
                        }}><X size={14}/></Button>
                      </div>
                      <Input placeholder="Notes (optionnel)" value={l.notes || ""} onChange={(e) => {
                        const licenses = [...editor.licenses]; licenses[i] = { ...l, notes: e.target.value };
                        setEditor({ ...editor, licenses });
                      }}/>
                    </div>
                  ))}
                </div>
              </div>
              {/* v295 — Upload fichiers possible DÈS la création (certaines licences
                   sont générées par fichier .lic/.key/.pem/zip). On met en file
                   d'attente côté client ; l'upload réel se fait après POST /software-licenses
                   dans `saveEditor` (via le endpoint existant /attachments). */}
              <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Pièces jointes <span className="normal-case text-muted-foreground/70 font-normal">(keyfile, certificat, PDF…)</span>
                  </div>
                  <label className="text-xs text-primary hover:underline cursor-pointer inline-flex items-center gap-1" data-testid="sl-editor-file-add">
                    <UploadSimple size={12}/> Ajouter un fichier
                    <input type="file" multiple className="hidden"
                           onChange={(e) => {
                             const files = Array.from(e.target.files || []);
                             e.target.value = "";
                             if (!files.length) return;
                             setEditor({ ...editor, pending_files: [...(editor.pending_files || []), ...files] });
                           }}/>
                  </label>
                </div>
                {(editor.pending_files || []).length === 0 ? (
                  <div className="text-[11px] text-muted-foreground">
                    Aucun fichier en attente. Les fichiers seront uploadés automatiquement à la {editor.mode === "create" ? "création" : "sauvegarde"}.
                  </div>
                ) : (
                  <div className="space-y-1" data-testid="sl-editor-file-list">
                    {editor.pending_files.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 py-1 text-sm" data-testid={`sl-editor-file-${i}`}>
                        <FileIcon size={14} className="text-slate-500 shrink-0"/>
                        <span className="flex-1 truncate">{f.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0">{Math.round(f.size / 1024)} Ko</span>
                        <button type="button"
                                onClick={() => setEditor({ ...editor, pending_files: editor.pending_files.filter((_, j) => j !== i) })}
                                className="text-red-600 hover:bg-red-50 p-1 rounded"
                                data-testid={`sl-editor-file-remove-${i}`}>
                          <X size={13}/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)} disabled={busy}>Annuler</Button>
            <Button onClick={saveEditor} disabled={busy} data-testid="sl-editor-save">
              {busy ? "…" : editor?.mode === "create" ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Email modal -------- */}
      <Dialog open={!!emailModal} onOpenChange={(v) => !v && setEmailModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Envoyer par email</DialogTitle>
            <DialogDescription>La fiche <b>{emailModal?.item?.title}</b> sera envoyée avec ses licences et liens. Les pièces jointes sont référencées mais restent hébergées dans MasterTicket.</DialogDescription>
          </DialogHeader>
          {emailModal && (
            <div className="space-y-3">
              <div>
                <Label>Destinataire (technicien)</Label>
                <Select value={emailModal.tech_id} onValueChange={(v) => setEmailModal({ ...emailModal, tech_id: v })}>
                  <SelectTrigger data-testid="sl-email-tech-select"><SelectValue placeholder="Choisir un technicien"/></SelectTrigger>
                  <SelectContent>
                    {techs.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.full_name} <span className="text-xs text-muted-foreground">— {t.email}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note personnelle (optionnel)</Label>
                <Textarea value={emailModal.note} onChange={(e) => setEmailModal({ ...emailModal, note: e.target.value })} rows={3} placeholder="Ex: Merci d'installer ça sur le poste d'Alice avant vendredi." data-testid="sl-email-note"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailModal(null)} disabled={busy}>Annuler</Button>
            <Button onClick={sendEmail} disabled={busy} data-testid="sl-email-send">
              {busy ? "Envoi…" : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Supprimer cette fiche ?"
        description={confirmDelete ? `« ${confirmDelete.title} » sera définitivement supprimée avec ses licences et pièces jointes. Action irréversible.` : ""}
        confirmText="Supprimer"
        variant="destructive"
        onConfirm={doDelete}
      />

      <ConfirmDialog
        open={confirmBulk}
        onOpenChange={setConfirmBulk}
        title={showArchived
          ? `Restaurer ${selectedIds.size} fiche${selectedIds.size > 1 ? "s" : ""} ?`
          : `Archiver ${selectedIds.size} fiche${selectedIds.size > 1 ? "s" : ""} ?`}
        description={showArchived
          ? "Les fiches sélectionnées redeviendront visibles dans la vue active."
          : "Les fiches archivées disparaissent de la vue principale mais restent accessibles via « Voir les archives »."}
        confirmText={showArchived ? "Restaurer" : "Archiver"}
        onConfirm={async () => {
          try {
            const r = await api.post("/software-licenses/bulk-archive", {
              ids: Array.from(selectedIds), archived: !showArchived,
            });
            toast.success(`${r.data.updated} fiche(s) ${showArchived ? "restaurée(s)" : "archivée(s)"}`);
            setSelectedIds(new Set()); setConfirmBulk(false); load();
          } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
        }}
      />
    </div>
  );
}
